const CHUNK_RELOAD_FLAG = 'chunk-load-error-reloaded-at';
/** 旧版布尔 flag，迁移时顺带清掉，避免残留干扰排查 */
const LEGACY_CHUNK_RELOAD_FLAG = 'chunk-load-error-reloaded';
/** 同一失败周期内只自动刷新一次，避免死循环或永久无法再刷 */
const RELOAD_COOLDOWN_MS = 15_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Unable to preload CSS',
  'Loading CSS chunk',
  'ChunkLoadError',
];

const TOAST_ATTR = 'data-chunk-load-error-toast';

/** 已自动刷新过仍失败时抛出，供路由 ErrorBoundary 展示友好页（不再二次 toast） */
export class ChunkLoadRecoveryError extends Error {
  constructor(message = '资源加载失败，请强制刷新页面或稍后重试') {
    super(message);
    this.name = 'ChunkLoadRecoveryError';
  }
}

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  // 恢复失败是业务包装错误，不应再走自动刷新
  if (error instanceof ChunkLoadRecoveryError) return false;

  const messages: string[] = [];

  if (typeof error === 'string') {
    messages.push(error);
  } else if (error instanceof Error) {
    messages.push(error.message);
    if (error.name) messages.push(error.name);
  } else if (typeof error === 'object') {
    const record = error as { message?: unknown; reason?: unknown; name?: unknown };
    if (typeof record.message === 'string') messages.push(record.message);
    if (typeof record.reason === 'string') messages.push(record.reason);
    if (typeof record.name === 'string') messages.push(record.name);
  }

  return messages.some((message) => CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern)));
}

function readReloadAt(): number {
  try {
    sessionStorage.removeItem(LEGACY_CHUNK_RELOAD_FLAG);
    const raw = sessionStorage.getItem(CHUNK_RELOAD_FLAG);
    if (!raw) return 0;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
}

function isChunkReloadInCooldown(): boolean {
  const last = readReloadAt();
  return last > 0 && Date.now() - last < RELOAD_COOLDOWN_MS;
}

/**
 * 部署后旧 chunk 404 / CSS preload 失败时刷新一次。
 * 使用时间戳冷却，避免绑 window.load 清 flag 导致死循环，或 flag 永不清。
 */
function reloadOnceForChunkError(): boolean {
  if (isChunkReloadInCooldown()) {
    return false;
  }

  try {
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage 不可用时仍尝试刷新
  }

  window.location.reload();
  return true;
}

/** 动态模块成功加载后清除冷却标记 */
function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    sessionStorage.removeItem(LEGACY_CHUNK_RELOAD_FLAG);
  } catch {
    // ignore
  }
}

/** 自动刷新已用尽时的轻量提示（不依赖 Arco） */
function notifyChunkLoadFailure(detail?: string): void {
  if (typeof document === 'undefined') return;

  console.error('[chunk] 资源加载失败且已自动刷新过，请手动强制刷新', detail || '');

  if (document.querySelector(`[${TOAST_ATTR}]`)) return;

  const el = document.createElement('div');
  el.setAttribute(TOAST_ATTR, '1');
  el.setAttribute('role', 'alert');
  el.textContent = '页面资源加载失败，请强制刷新（Ctrl/Cmd+Shift+R）或稍后重试';
  el.style.cssText = [
    'position:fixed',
    'z-index:100000',
    'left:50%',
    'bottom:24px',
    'transform:translateX(-50%)',
    'max-width:min(480px,calc(100vw - 32px))',
    'padding:12px 16px',
    'border-radius:8px',
    'background:#1d2129',
    'color:#fff',
    'font-size:13px',
    'line-height:1.5',
    'box-shadow:0 6px 16px rgba(0,0,0,.2)',
  ].join(';');
  document.body.appendChild(el);
}

/**
 * 静态资源 / 未进入路由边界的失败：优先自动刷新；冷却期内 toast。
 * @returns true 表示已触发刷新
 */
export function handleChunkLoadFailure(detail?: string): boolean {
  if (reloadOnceForChunkError()) {
    console.warn('[chunk] 静态/动态资源加载失败，自动刷新以获取最新资源', detail || '');
    return true;
  }
  notifyChunkLoadFailure(detail);
  return false;
}

/**
 * lazy import 失败：先短间隔重试（消化偶发 preload/网络抖动），
 * 仍失败再自动刷新；冷却内抛 ChunkLoadRecoveryError 给 ErrorBoundary。
 */
export async function importWithChunkReload<T>(importer: () => Promise<T>): Promise<T> {
  /** 首次失败后再试次数（合计最多 3 次） */
  const maxRetries = 2;
  const retryBaseMs = 400;
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const module = await importer();
      clearChunkReloadFlag();
      return module;
    } catch (error) {
      lastError = error;
      if (!isChunkLoadError(error)) {
        throw error;
      }
      if (attempt >= maxRetries) {
        break;
      }
      const waitMs = retryBaseMs * (attempt + 1);
      const detail = error instanceof Error ? error.message : String(error);
      console.warn(`[chunk] 动态导入失败，${waitMs}ms 后重试 (${attempt + 1}/${maxRetries})`, detail);
      await new Promise((resolve) => {
        window.setTimeout(resolve, waitMs);
      });
    }
  }

  const detail = lastError instanceof Error ? lastError.message : String(lastError);
  if (reloadOnceForChunkError()) {
    console.warn('[chunk] 动态模块重试仍失败，自动刷新以获取最新资源', detail);
    return new Promise(() => undefined as never);
  }
  throw new ChunkLoadRecoveryError();
}
